import { Item } from "@aitisal/contracts/restaurant/menu";
import { Button } from "@aitisal/ui/retail-storefront/button";
import { useNavigate } from "@tanstack/react-router";
import { ReactNode } from "react";
import { getTitle } from "../../helper/helper";
import { useActiveMenu } from "../../hooks/useActiveMenu";
import ProductCard from "./ProductCard";

interface SectionGridProps {
  startIndex?: number;
  maxSections?: number;
}

function Container({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      {children}
    </div>
  );
}

export default function SectionGrid({
  startIndex = 0,
  maxSections,
}: SectionGridProps) {
  const navigate = useNavigate();
  const { sections } = useActiveMenu();

  const slicedSections = maxSections
    ? sections.slice(startIndex, startIndex + maxSections)
    : sections.slice(startIndex);

  const handleViewAll = (sectionTitle: string) => {
    navigate({
      to: "/c/shop",
      search: { category: sectionTitle, search: undefined },
    });
  };

  if (slicedSections.length === 0) {
    return null;
  }

  return (
    <div className="w-full bg-white">
      {slicedSections.map((section) => {
        const validItems = (section?.items || []).filter(
          (item: Item) => item.isSeparatelySellable,
        );

        if (validItems.length === 0) {
          return null;
        }

        const sectionTitle = getTitle(section.name) || "Our Collections";

        return (
          <Container key={section.sectionId}>
            <div className="flex flex-col gap-6 pt-8 pb-12">
              <div className="flex flex-row items-center justify-between">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                  {sectionTitle}
                </h2>
                <Button
                  onClick={() => handleViewAll(sectionTitle)}
                  variant="outline"
                  className="rounded-full px-4 py-1.5 bg-white border-2 border-gray-300 hover:bg-gray-50 shadow-md text-xs md:text-sm font-medium text-gray-700"
                >
                  View All
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {validItems.map((item) => (
                  <ProductCard key={item.itemId} item={item} isNew={false} />
                ))}
              </div>
            </div>
          </Container>
        );
      })}
    </div>
  );
}
